import type { ReactNode } from "react";

import { Spinner } from "@/components/ui/spinner";
import { useTranslation } from "@/stores/locale";
import { useDelayed } from "@/lib/use-delayed";
import type { TranslationKey } from "@/lib/i18n";
import { cn } from "@/lib/utils";

/**
 * What is happening, so the message can say it.
 *
 * "Loading…" on a screen where somebody just pressed Search is a worse answer
 * than "Searching members…": the wait is the same, but one of them tells you
 * your action was received.
 */
export type LoadingAction =
  | "loading"
  | "searching"
  | "filtering"
  | "sorting"
  | "paging"
  | "saving"
  | "deleting"
  | "exporting";

const MESSAGES: Record<LoadingAction, TranslationKey> = {
  loading: "load.loading",
  searching: "load.searching",
  filtering: "load.filtering",
  sorting: "load.sorting",
  paging: "load.paging",
  saving: "load.saving",
  deleting: "load.deleting",
  exporting: "load.exporting",
};

/**
 * The line that says what the wait is for.
 *
 * Deliberately quiet — a spinner at caption size beside a sentence, not a
 * centred overlay. The skeleton beside it is doing the work of showing where
 * the content will land; this only names the action, for the case where the
 * wait is long enough to wonder about.
 */
export function LoadingNote({
  action = "loading",
  subject,
  className,
}: {
  action?: LoadingAction;
  /** What is being acted on: "members", "clubs". Left out for a bare message. */
  subject?: string | undefined;
  className?: string | undefined;
}) {
  const t = useTranslation();
  const message = t(MESSAGES[action]);
  return (
    <p
      role="status"
      className={cn(
        "text-fg-subtle text-footnote flex items-center gap-2 tabular-nums",
        className,
      )}
    >
      <Spinner className="size-3.5 shrink-0" />
      {subject
        ? message.replace("{subject}", subject.toLowerCase())
        : message.replace(" {subject}", "")}
    </p>
  );
}

/**
 * A page's own loading state: its skeleton, with a note saying what for.
 *
 * `children` is the skeleton, and it is the caller's because it has to be the
 * shape of that page — a table's rows, a map's canvas, a form's fields.
 * Nothing generic can hold the right space, and holding the right space is the
 * whole job.
 *
 * Shows nothing at all until the wait is worth mentioning; see `useDelayed`.
 */
export function Loading({
  busy,
  action = "loading",
  subject,
  children,
  className,
}: {
  busy: boolean;
  action?: LoadingAction;
  subject?: string | undefined;
  children: ReactNode;
  className?: string | undefined;
}) {
  const show = useDelayed(busy);
  if (!busy) return null;
  return (
    <div aria-busy className={cn("grid gap-3", className)}>
      {show ? <LoadingNote action={action} subject={subject} /> : null}
      {children}
    </div>
  );
}
